import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useConversation } from "@elevenlabs/react";
import { Mic, PhoneOff, Loader2, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AGENTS = {
  health: { title: "Health & Wellness Advisor", emoji: "❤️", accent: "#e23e57", bg: "#fff1f3" },
  games: { title: "Daily Games", emoji: "🎲", accent: "#2563eb", bg: "#eef4ff" },
};

function VoiceRoom({ agentKey, uid }) {
  const meta = AGENTS[agentKey] || { title: "Elderra Voice", emoji: "🎙️", accent: "#6d28d9", bg: "#f3f0ff" };
  const [phase, setPhase] = useState("idle"); // idle | connecting | active | error
  const [errorMsg, setErrorMsg] = useState("");
  const [label, setLabel] = useState(meta.title);
  const sessionIdRef = useRef(null);
  const convRef = useRef(null);

  const logEnd = useCallback(() => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    sessionIdRef.current = null;
    const body = JSON.stringify({ session_id: sid });
    // beacon survives page unload; fall back to fetch
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/api/convai/session/end`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/api/convai/session/end`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  }, []);

  const conversation = useConversation({
    onConnect: async () => {
      setPhase("active");
      try {
        const res = await fetch(`${API}/api/convai/session/start`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent: agentKey, uid: uid || null }),
        });
        if (res.ok) sessionIdRef.current = (await res.json()).session_id;
      } catch (_) {}
    },
    onDisconnect: () => { logEnd(); setPhase("idle"); },
    onError: (e) => {
      const m = typeof e === "string" ? e : (e?.message || "");
      if (/cancel/i.test(m)) return; // benign teardown race, not a real failure
      setErrorMsg(m || "Something went wrong. Please try again.");
      setPhase("error");
    },
  });
  const speaking = conversation.isSpeaking;
  convRef.current = conversation;

  const start = useCallback(async () => {
    setErrorMsg("");
    setPhase("connecting");
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const res = await fetch(`${API}/api/convai/signed-url/${agentKey}`);
      if (!res.ok) throw new Error("Could not start the voice agent right now. Please try again shortly.");
      const data = await res.json();
      if (data.label) setLabel(data.label);
      await conversation.startSession({ signedUrl: data.signed_url });
    } catch (e) {
      setErrorMsg(
        e?.name === "NotAllowedError"
          ? "Please allow microphone access, then tap Start again."
          : (e?.message || "Could not connect. Please try again.")
      );
      setPhase("error");
    }
  }, [agentKey, conversation]);

  const stop = useCallback(async () => {
    try { await conversation.endSession(); } catch (_) {}
    setPhase("idle");
  }, [conversation]);

  // Teardown ONLY on real unmount. Depending on `conversation` here would
  // re-run this cleanup on every render (the hook returns a new object each
  // time) and call endSession mid-connect → "Session cancelled during
  // connection". Use a ref + empty deps so it fires once, on navigate-away.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { logEnd(); convRef.current?.endSession?.().catch(() => {}); }, []);

  const active = phase === "active";
  const connecting = phase === "connecting";

  return (
    <div style={{ minHeight: "100vh", background: meta.bg, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "system-ui, sans-serif" }}>
      <a href="/" data-testid="voice-back-link"
         style={{ position: "absolute", top: 20, left: 20, color: meta.accent, textDecoration: "none",
                  display: "flex", alignItems: "center", gap: 6, fontSize: 16, fontWeight: 600 }}>
        <ArrowLeft size={20} /> Back
      </a>

      <div style={{ fontSize: 72, marginBottom: 8 }} aria-hidden>{meta.emoji}</div>
      <h1 data-testid="voice-agent-title" style={{ fontSize: 30, fontWeight: 800, color: "#1f2937", margin: "0 0 6px", textAlign: "center" }}>
        {label}
      </h1>
      <p style={{ color: "#4b5563", fontSize: 18, margin: "0 0 36px", textAlign: "center", maxWidth: 420 }}>
        {active ? (speaking ? "Listening to the advisor…" : "Go ahead, speak now.")
                : connecting ? "Connecting…"
                : "Tap the button, allow your microphone, and just talk."}
      </p>

      {active && (
        <div aria-hidden style={{ width: 160, height: 160, borderRadius: "50%", marginBottom: 32,
             background: meta.accent, opacity: speaking ? 1 : 0.65, display: "flex", alignItems: "center",
             justifyContent: "center", transition: "transform .3s, opacity .3s",
             transform: speaking ? "scale(1.08)" : "scale(1)",
             boxShadow: `0 0 0 ${speaking ? 24 : 12}px ${meta.accent}22` }}>
          <Mic size={64} color="#fff" />
        </div>
      )}

      {!active && (
        <button data-testid="voice-start-btn" onClick={start} disabled={connecting}
          style={{ display: "flex", alignItems: "center", gap: 12, background: meta.accent, color: "#fff",
                   border: "none", borderRadius: 999, padding: "20px 40px", fontSize: 22, fontWeight: 700,
                   cursor: connecting ? "wait" : "pointer", boxShadow: `0 10px 30px ${meta.accent}55` }}>
          {connecting ? <Loader2 size={28} className="animate-spin" /> : <Mic size={28} />}
          {connecting ? "Connecting…" : "Start talking"}
        </button>
      )}

      {active && (
        <button data-testid="voice-end-btn" onClick={stop}
          style={{ display: "flex", alignItems: "center", gap: 10, background: "#dc2626", color: "#fff",
                   border: "none", borderRadius: 999, padding: "16px 34px", fontSize: 20, fontWeight: 700, cursor: "pointer" }}>
          <PhoneOff size={24} /> End call
        </button>
      )}

      {phase === "error" && (
        <p data-testid="voice-error" style={{ color: "#b91c1c", marginTop: 24, fontSize: 17, textAlign: "center", maxWidth: 420 }}>
          {errorMsg}
        </p>
      )}
    </div>
  );
}

export default function VoiceAgent() {
  const { agent } = useParams();
  const [params] = useSearchParams();
  return <VoiceRoom agentKey={agent} uid={params.get("u") || ""} />;
}
